import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SocialFollow } from '@/components/ui/social-follow';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import LiveChatModal from '@/components/chat/LiveChatModal';
import footerLogo from '@/assets/odyvoy-logo-full.png';
import '@/styles/help.css';

import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  MessageCircle,
  Mail,
  Search,
  ArrowUpRight,
  LifeBuoy,
  CreditCard,
  Settings2,
  Shield,
  Zap,
  ChevronDown,
  Send,
  ArrowLeft,
  ArrowRight,
  Moon,
  Sun,
} from 'lucide-react';

const faqItems = [
  { question: "How do I upgrade my subscription plan?", answer: "You can upgrade your plan anytime from the Subscription tab. Choose your desired plan and follow the checkout process. Your new features will be available immediately.", category: "Billing" },
  { question: "Can I cancel my subscription at any time?", answer: "Yes, you can cancel your subscription anytime through the Subscription tab or customer portal. Your access will continue until the end of your billing period.", category: "Billing" },
  { question: "What payment methods do you accept?", answer: "We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure Stripe payment processing.", category: "Billing" },
  { question: "How does the packing assistant work?", answer: "Our AI-powered packing assistant analyzes your destination, travel dates, weather, and activities to suggest personalized packing lists. Premium users get advanced recommendations.", category: "Features" },
  { question: "Is my travel data secure?", answer: "Yes, we use industry-standard encryption and security measures. Your data is stored securely and never shared with third parties without your consent.", category: "Privacy" },
  { question: "How accurate is the currency converter?", answer: "Our currency converter uses real-time exchange rates updated every few minutes.", category: "Features" },
  { question: "Can I share my trip plans with friends?", answer: "Yes! You can share trip details, packing lists, and expense groups with other users. This feature is available on all subscription tiers.", category: "Features" },
  { question: "How do I track group expenses?", answer: "Create an expense group from your trip dashboard, invite members, and start logging shared expenses. Our smart split calculator helps divide costs fairly.", category: "Features" },
  { question: "What are boost passes?", answer: "Boost passes give you temporary access to premium features for a specific duration (3, 7, or 14 days). Perfect for occasional travelers who don't need a full subscription.", category: "Billing" },
  { question: "How do I export my travel data?", answer: "Premium users can export all their trip data, expenses, and documents through the Data Management section in Account Settings.", category: "Account" },
  { question: "Can I use OdyVoy offline?", answer: "Some features like viewing saved trips and packing lists work offline with the First Class Pack subscription. Features requiring real-time data (currency rates, weather) need an internet connection.", category: "Features" },
  { question: "How do I reset my password?", answer: "Click 'Forgot Password' on the login page. We'll send a reset link to your registered email address. Follow the link to create a new password.", category: "Account" },
  { question: "What's included in the free Economy plan?", answer: "The Economy plan includes basic trip planning, packing lists, visa requirements check, expense tracking, and access to our AI travel assistant with usage limits.", category: "Billing" },
  { question: "How do I contact customer support?", answer: "You can reach us via live chat (available 24/7), email at info@odyvoy.com, or through the contact form in the Support Center.", category: "Support" },
  { question: "Can I get a refund?", answer: "At this time, we do not offer refunds on subscriptions. We encourage you to explore our features using the free Economy plan or try a boost pass before committing to a paid subscription. If you have concerns about your subscription, please contact our support team.", category: "Billing" },
];

const categoryIcons = { Billing: CreditCard, Features: Zap, Privacy: Shield, Account: Settings2, Support: LifeBuoy };

const contactSchema = z.object({
  subject: z.string().trim().min(3, "Subject must be at least 3 characters").max(100, "Subject must be less than 100 characters"),
  category: z.string().min(1, "Please select a category"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000, "Message must be less than 1000 characters"),
});
type ContactFormData = z.infer<typeof contactSchema>;

const Help = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [initialChatMessage, setInitialChatMessage] = useState<string>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [openQuestion, setOpenQuestion] = useState<string | null>(faqItems[0]?.question ?? null);

  const categories = useMemo(() => ['All', ...Array.from(new Set(faqItems.map((i) => i.category)))], []);

  const filteredFAQs = useMemo(() => {
    return faqItems.filter((item) => {
      const matchesCat = activeCategory === 'All' || item.category === activeCategory;
      const q = searchQuery.trim().toLowerCase();
      const matchesQuery = !q || item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [searchQuery, activeCategory]);

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { category: 'General Question' },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: { subject: data.subject, category: data.category, message: data.message, userEmail: user?.email },
      });
      if (error) throw error;
      toast({ title: 'Message sent!', description: "We'll get back to you as soon as possible." });
      reset();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({ title: 'Error', description: 'Failed to send message. Please try again or email us directly at info@odyvoy.com', variant: 'destructive' });
    }
  };

  return (
    <div className="help-page min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Help & Support Center | OdyVoy - Travel Planning Assistance</title>
        <meta name="description" content="Get help with OdyVoy. FAQs, quick start guides, live chat, and email support for packing, budgets, and trip planning." />
        <link rel="canonical" href="https://odyvoy.com/help" />
        <meta property="og:title" content="Help & Support Center | OdyVoy" />
        <meta property="og:description" content="Get help with OdyVoy travel planning app. Access FAQs, guides, and support." />
        <meta property="og:url" content="https://odyvoy.com/help" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            })),
          })}
        </script>
      </Helmet>

      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex justify-between items-center h-16 md:h-20" aria-label="Help navigation">
            <Link to={user ? '/dashboard' : '/'} className="no-underline inline-flex items-center gap-2 min-h-12 text-sm sm:text-base font-medium text-foreground hover:text-primary transition-colors">
              <ArrowLeft className="h-4 w-4" />
              {user ? 'Back to Dashboard' : 'Home'}
            </Link>
            <div className="flex items-center gap-1 sm:gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Toggle color theme" className="relative h-12 w-12 text-primary hover:text-primary hover:bg-primary/10">
                    <Moon className="h-[18px] w-[18px] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Sun className="absolute h-[18px] w-[18px] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setTheme('light')}>Light</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('dark')}>Dark</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')}>System</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to={user ? '/dashboard' : '/auth'} className="no-underline inline-flex items-center gap-2 min-h-12 text-sm sm:text-base font-medium text-primary hover:text-primary transition-colors">
                {user ? 'My trips' : 'Log In'}<ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </nav>
        </div>
      </header>

      <main className="help-container">
        <div className="help-grid">
          <aside className="help-intro">
            <p className="help-kicker">Help Center</p>
            <div className="help-kicker-rule" aria-hidden="true" />
            <div className="help-brand" aria-hidden="true">
              <img src="/lovable-uploads/odyvoy-icon.png" alt="" width={104} height={104} />
              <div>
                <p className="help-brand-name">OdyVoy</p>
                <p className="help-brand-tagline">Your Odyssey. Our Voyage!</p>
              </div>
            </div>
            <h1 className="help-heading">How can we<span>help you?</span></h1>
            <p className="help-lede">Find answers about your account, plans and travel tools, or talk to our AI assistant.</p>
            <div className="help-search">
              <Search className="h-5 w-5" aria-hidden="true" />
              <Input aria-label="Search questions or topics" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search questions or topics…" />
            </div>
            <div className="help-support-links">
              <Button variant="ghost" className="help-support-link" onClick={() => { setInitialChatMessage(undefined); setIsChatOpen(true); }}>
                <span className="help-icon help-icon-solid"><MessageCircle className="h-4 w-4" /></span>
                <span className="help-support-copy"><strong>Live AI Chat</strong><small>Instant answers, 24/7</small></span>
                <ArrowUpRight className="h-4 w-4 shrink-0" />
              </Button>
              <Button variant="ghost" asChild className="help-support-link">
                <a href="mailto:info@odyvoy.com">
                  <span className="help-icon"><Mail className="h-4 w-4" /></span>
                  <span className="help-support-copy"><strong className="underline">Email Support</strong><small className="underline">info@odyvoy.com</small></span>
                  <ArrowUpRight className="h-4 w-4 shrink-0" />
                </a>
              </Button>
            </div>
          </aside>
          <section id="faqs" aria-label="Frequently asked questions" className="min-w-0">
            <div className="help-categories" aria-label="FAQ categories">
              {categories.map((cat) => <Button key={cat} variant={activeCategory === cat ? 'default' : 'ghost'} aria-label={`Filter FAQs by ${cat} category`} aria-pressed={activeCategory === cat} className="help-category" onClick={() => { setActiveCategory(cat); setOpenQuestion(null); }}>{cat}</Button>)}
            </div>
            <div className="help-faq-list">
              {filteredFAQs.length === 0 && <div className="border border-dashed border-border rounded p-8 text-center" role="status">
                <p className="text-sm text-muted-foreground">No results — try another search or ask our AI directly.</p>
                <Button className="mt-4 min-h-12" onClick={() => { setInitialChatMessage(searchQuery); setIsChatOpen(true); }}>Ask AI assistant</Button>
              </div>}
              {filteredFAQs.map((item) => {
                const Icon = categoryIcons[item.category as keyof typeof categoryIcons] ?? LifeBuoy;
                const isOpen = openQuestion === item.question;
                const id = `faq-${faqItems.indexOf(item)}`;
                return <article className="help-faq" key={item.question} data-open={isOpen}>
                  <h2><Button variant="ghost" className="help-question" id={`${id}-trigger`} aria-expanded={isOpen} aria-controls={`${id}-answer`} onClick={() => setOpenQuestion(isOpen ? null : item.question)}>
                    <span className="help-icon"><Icon className="h-4 w-4" /></span>
                    <span className="flex-1 min-w-0">{item.question}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`} />
                  </Button></h2>
                  {isOpen && <div className="help-answer" id={`${id}-answer`} role="region" aria-labelledby={`${id}-trigger`}>
                    <p>{item.answer}</p>
                    <Button variant="ghost" className="help-ask" onClick={() => { setInitialChatMessage(item.question); setIsChatOpen(true); }}><MessageCircle className="h-4 w-4" />Ask AI for more detail</Button>
                  </div>}
                </article>;
              })}
            </div>
          </section>
        </div>
        <section className="help-contact help-grid" aria-labelledby="contact-heading">
          <div>
            <span className="text-primary text-xs font-bold">Contact us</span>
            <h2 id="contact-heading">Still need a hand?</h2>
            <p>Send us a message and we'll get back as soon as possible.</p>
            <a className="help-email" href="mailto:info@odyvoy.com"><Mail className="h-4 w-4" />info@odyvoy.com</a>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="help-subject">Subject *</label>
                <Input id="help-subject" {...register('subject')} placeholder="Brief description" disabled={isSubmitting} className="min-h-12" aria-invalid={!!errors.subject} aria-describedby={errors.subject ? 'help-subject-error' : undefined} />
                {errors.subject && <p id="help-subject-error" className="text-xs text-destructive" role="alert">{errors.subject.message}</p>}
              </div>
              <div>
                <label htmlFor="help-contact-category">Category *</label>
                <select id="help-contact-category" {...register('category')} className="w-full min-h-12 border border-input px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" disabled={isSubmitting}>
                  <option>General Question</option><option>Billing & Payments</option><option>Technical Issue</option><option>Feature Request</option><option>Bug Report</option>
                </select>
                {errors.category && <p className="text-xs text-destructive" role="alert">{errors.category.message}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="help-message">Message *</label>
              <Textarea id="help-message" {...register('message')} placeholder="Tell us what's going on…" className="min-h-[140px]" disabled={isSubmitting} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'help-message-error' : undefined} />
              {errors.message && <p id="help-message-error" className="text-xs text-destructive" role="alert">{errors.message.message}</p>}
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full min-h-12 gap-2 rounded">
              <Send className="h-4 w-4" />{isSubmitting ? 'Sending…' : 'Send Message'}
            </Button>
          </form>
        </section>
        <footer className="help-footer">
          <div className="help-footer-brand">
            <img src={footerLogo} alt="OdyVoy — Your Odyssey. Our Voyage!" />
            <div className="help-social"><SocialFollow /></div>
            <p>© {new Date().getFullYear()} OdyVoy</p>
          </div>
          <Button variant="ghost" className="min-h-12 gap-2 text-primary" onClick={() => navigate(user ? '/dashboard' : '/')}>
            {user ? 'Back to Dashboard' : 'Back to Home'}<ArrowUpRight className="h-4 w-4" />
          </Button>
        </footer>
      </main>

      <LiveChatModal
        open={isChatOpen}
        onOpenChange={(open) => {
          setIsChatOpen(open);
          if (!open) setInitialChatMessage(undefined);
        }}
        initialMessage={initialChatMessage}
      />
    </div>
  );
};

export default Help;
